/**
 * Web twin of native `TaskMapView`: Google Maps with a photo marker per task,
 * fitted to every marker plus the bucket's geofence circle.
 *
 * Needs `VITE_GOOGLE_MAPS_API_KEY` (Maps JavaScript API). Photo markers are
 * Advanced Markers, which need a map ID; `DEMO_MAP_ID` serves until
 * `VITE_GOOGLE_MAPS_MAP_ID` is set.
 */
import {
  Component,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  APILoadingStatus,
  APIProvider,
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  Circle,
  Map,
  useApiLoadingStatus,
  useMap,
} from '@vis.gl/react-google-maps';
import type { GeoPoint } from '../bucketSettings';
import { geofenceEdges, spreadOverlapping } from '../taskMapLayout';
import TaskMapMarker from './TaskMapMarker';

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim() ?? '';
const MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID?.trim() || 'DEMO_MAP_ID';
/** Native SINGLE_POINT_DELTA 0.01 is about this zoom. */
const SINGLE_POINT_ZOOM = 15;
/** Room for the floating footer and + under the fitted markers. */
const FIT_PADDING = { top: 72, right: 32, bottom: 180, left: 32 };
const PRIMARY = 'rgba(79, 215, 223, 1)';
/** A map that has drawn nothing by now is not coming; use the embed instead. */
const TILES_TIMEOUT_MS = 10000;

export function hasMapsKey(): boolean {
  return API_KEY.length > 0;
}

/*
 * A rejected key (API not enabled, referrer not allowed) leaves Google's grey
 * error panel, and remounting markers then throws. Once that happens the
 * page uses the keyless embed for the rest of the session.
 */
let mapsFailed = false;
const failureListeners = new Set<() => void>();

function markMapsFailed() {
  if (mapsFailed) return;
  mapsFailed = true;
  failureListeners.forEach((listener) => listener());
}

if (typeof window !== 'undefined') {
  (window as unknown as { gm_authFailure?: () => void }).gm_authFailure = markMapsFailed;
}

export function useMapsFailed(): boolean {
  return useSyncExternalStore(
    (listener) => {
      failureListeners.add(listener);
      return () => failureListeners.delete(listener);
    },
    () => mapsFailed,
  );
}

class MapBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    markMapsFailed();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

export interface MapTask {
  id: string;
  title: string;
  status: string;
  photoUrl: string | null;
  location: GeoPoint;
}

export interface MapGeofence {
  location: GeoPoint;
  radius: number;
}

interface Props {
  tasks: MapTask[];
  geofence?: MapGeofence | null;
  onOpen: (task: MapTask) => void;
  className?: string;
  /**
   * Task detail overlays its own pin so it does not wait on tiles — or on a
   * second embed if tiles are late. The bucket map still draws these.
   */
  showMarkers?: boolean;
}

function WatchApiStatus() {
  const status = useApiLoadingStatus();
  useEffect(() => {
    if (status === APILoadingStatus.AUTH_FAILURE || status === APILoadingStatus.FAILED) {
      markMapsFailed();
    }
  }, [status]);
  return null;
}

function FitToContent({ points }: { points: Array<{ lat: number; lng: number }> }) {
  const map = useMap();
  const key = points.map((p) => `${p.lat},${p.lng}`).join('|');

  useEffect(() => {
    if (!map || points.length === 0) return;
    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(SINGLE_POINT_ZOOM);
      return;
    }
    const lats = points.map((p) => p.lat);
    const lngs = points.map((p) => p.lng);
    map.fitBounds(
      {
        north: Math.max(...lats),
        south: Math.min(...lats),
        east: Math.max(...lngs),
        west: Math.min(...lngs),
      },
      FIT_PADDING,
    );
    // `key` carries the points; the array is new every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);

  return null;
}

export default function TaskMap({
  tasks,
  geofence = null,
  onOpen,
  className,
  showMarkers = true,
}: Props) {
  const entries = useMemo(() => spreadOverlapping(tasks), [tasks]);
  const points = useMemo(
    () => [
      ...entries.map((entry) => entry.position),
      ...(geofence ? geofenceEdges(geofence.location, geofence.radius) : []),
    ],
    [entries, geofence],
  );
  const center = points[0] ?? { lat: 0, lng: 0 };
  const [tiles, setTiles] = useState(false);

  useEffect(() => {
    if (!showMarkers || tiles) return;
    const timer = window.setTimeout(markMapsFailed, TILES_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [showMarkers, tiles]);

  return (
    <MapBoundary>
      <APIProvider apiKey={API_KEY} onError={markMapsFailed}>
        <WatchApiStatus />
        <Map
          {...(showMarkers
            ? { mapId: MAP_ID }
            : {
                // Raster styles (no map ID): hide Google's own pins so the
                // overlay TaskMapMarker is the only mark on a task location.
                styles: [
                  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
                ],
              })}
          defaultCenter={center}
          defaultZoom={SINGLE_POINT_ZOOM}
          gestureHandling="greedy"
          disableDefaultUI
          clickableIcons={false}
          className={className}
          onTilesLoaded={() => setTiles(true)}
        >
          {/* Markers wait for tiles: a rejected key never draws any, so nothing mounts to break. */}
          {tiles && geofence ? (
            <Circle
              center={{ lat: geofence.location.latitude, lng: geofence.location.longitude }}
              radius={geofence.radius}
              strokeColor={PRIMARY}
              strokeWeight={4}
              fillOpacity={0}
              clickable={false}
            />
          ) : null}
          {(showMarkers && tiles ? entries : []).map(({ item, position }) => (
            <AdvancedMarker
              key={item.id}
              position={position}
              title={item.title}
              anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM_CENTER}
              onClick={() => onOpen(item)}
            >
              <TaskMapMarker photoUrl={item.photoUrl} status={item.status} title={item.title} />
            </AdvancedMarker>
          ))}
          <FitToContent points={points} />
        </Map>
      </APIProvider>
    </MapBoundary>
  );
}
